
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import Editor from '@/components/Editor.vue';
import UserGroups from '@/components/UserGroups.vue';
import API from '@/lib/API';
import {
  Operation, OperationChild, User, AssignmentType, AssignmentAssignee, Lead,
} from '@/types';
import { currentUserHasPermission, getLeadCustomValueText, getAssignmentLabel } from '@/helpers';

moment.locale('it');

const typeConfig: Record<string, { title: string; statusLabel: string; assigneeLabel: string; prefix: string; userGroupIds: number[] }> = {
  practice: {
    title: 'Assegnatario delle pratiche di connessione',
    statusLabel: "Stato dell'assegnazione delle pratiche",
    assigneeLabel: 'Assegnatario delle pratiche di connessione',
    prefix: 'practice',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  project: {
    title: 'Assegnatario del progetto',
    statusLabel: "Stato dell'assegnazione del progetto",
    assigneeLabel: 'Assegnatario del progetto',
    prefix: 'project',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  enea: {
    title: 'Assegnatario ENEA/GSE/Altro',
    statusLabel: "Stato dell'assegnazione ENEA/GSE/Altro",
    assigneeLabel: 'Assegnatario ENEA/GSE/Altro',
    prefix: 'enea',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  dogane: {
    title: 'Assegnatario Dogane',
    statusLabel: "Stato dell'assegnazione Dogane",
    assigneeLabel: 'Assegnatario Dogane',
    prefix: 'dogane',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  installer: {
    title: 'Assegnatario installatore',
    statusLabel: "Stato dell'assegnazione installatore",
    assigneeLabel: 'Installatore',
    prefix: 'installer',
    // Gruppo 1 = "Installatori Stone Pine" (vedi backend models/assignment.js)
    userGroupIds: [1],
  },
};

interface MultiAssigneeEntry {
  assigneeId: number | null;
  status: 'todo' | 'done';
  assignedDate: Date | string | null;
}

@Component({
  components: {
    DatePickerDialog,
    Editor,
    UserGroups,
  },
})
export default class OperationAssignmentsDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop({ type: String, default: 'practice' }) readonly assignmentType!: string;
  @Prop(Number) readonly childId!: number;
  @Prop(Object) readonly operation!: Operation;
  @Prop({ type: Boolean, default: false }) readonly hideNotes!: boolean;

  // Quando true, il dialog non esegue API né optimistic commit:
  // emette `save` col body pronto ed è il genitore (TaskV/TaskR)
  // a staggarlo tramite updateTaskFields + UpdateButton.
  @Prop({ type: Boolean, default: false }) readonly deferSave!: boolean;

  dialog = false
  isNa = false

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerTargetIndex: number | null = null

  // Singolo assegnatario
  assigneeId: number | null = null
  assignedStatus = 'todo'
  assignedDate: Date | null = null

  initialAssigneeId: number | null = null
  initialAssignedStatus = 'todo'
  initialAssignedDate: Date | null = null

  // Multi-assegnatario
  assigneesList: MultiAssigneeEntry[] = []
  initialAssigneesJson = '[]'
  initialIsNa = false

  // Note
  notesValue = ''
  initialNotesValue = ''
  loadingNotes = false

  assignedValues = [
    { text: 'Da assegnare', value: 'todo' },
    { text: 'Assegnato', value: 'done' },
    { text: 'Non applicabile', value: 'na' },
  ]

  // Stessa terminologia dei tipi a singolo assegnatario (assignedValues):
  // per ogni installatore "Da assegnare" / "Assegnato" (lo stato "Non
  // applicabile" è a livello di incarico, gestito dallo switch dedicato).
  multiAssignedValues = [
    { text: 'Da assegnare', value: 'todo' },
    { text: 'Assegnato', value: 'done' },
  ]

  get config() {
    return typeConfig[this.assignmentType] || typeConfig.practice;
  }

  get assignmentTypeConfig(): AssignmentType | undefined {
    return this.$store.getters.getAssignmentType(this.assignmentType);
  }

  get isMultiple(): boolean {
    return this.assignmentTypeConfig?.multipleAssignees === true;
  }

  get dialogTitle(): string {
    const cfg = this.assignmentTypeConfig;
    return cfg ? getAssignmentLabel(cfg, this.operationChild?.type ?? null) : this.config.title;
  }

  get statusLabel(): string {
    const cfg = this.assignmentTypeConfig;
    const ct = this.operationChild?.type ?? null;
    return cfg
      ? `Stato dell'assegnazione: ${getAssignmentLabel(cfg, ct)}`
      : this.config.statusLabel;
  }

  get assigneeLabel(): string {
    const cfg = this.assignmentTypeConfig;
    const ct = this.operationChild?.type ?? null;
    return cfg ? getAssignmentLabel(cfg, ct) : this.config.assigneeLabel;
  }
  get prefix(): string { return this.config.prefix; }

  get operationChild(): OperationChild | null {
    return this.operation?.children
      && this.childId in this.operation.children
      && this.operation.children[this.childId]
      ? this.operation.children[this.childId] : null;
  }

  get assigneeUsers(): User[] {
    const groupIds = this.assignmentTypeConfig?.userGroupIds || this.config.userGroupIds;
    return this.$store.getters.users
      .filter((u: User) => {
        const userGroups = u.groups.map((g) => g.id);
        return groupIds.some((gid) => userGroups.includes(gid));
      });
  }

  get notesCustomValueName(): string | null {
    const config = this.assignmentTypeConfig;
    if (!config?.notesCustomValues) return null;
    const childType = this.operationChild?.type;
    if (!childType) return null;
    return config.notesCustomValues[childType] ?? null;
  }

  get notesLabel(): string {
    const config = this.assignmentTypeConfig;
    const childType = this.operationChild?.type;
    if (childType && config?.notesLabels?.[childType]) return config.notesLabels[childType];
    return 'Note';
  }

  get notesPermission(): { show: string; edit: string } | null {
    const config = this.assignmentTypeConfig;
    if (!config?.notesPermissions) return null;
    const childType = this.operationChild?.type;
    if (!childType) return null;
    return config.notesPermissions[childType] ?? null;
  }

  get canShowNotes(): boolean {
    if (this.hideNotes) return false;
    return !!this.notesCustomValueName && !!this.notesPermission
      && currentUserHasPermission(this.notesPermission.show);
  }

  get canEditNotes(): boolean {
    return !!this.notesPermission
      && currentUserHasPermission(this.notesPermission.edit);
  }

  get isDirty(): boolean {
    if (this.canEditNotes && this.notesValue !== this.initialNotesValue) return true;
    if (this.isMultiple) {
      if (this.isNa !== this.initialIsNa) return true;
      if (this.isNa) return false;
      return JSON.stringify(this.assigneesList) !== this.initialAssigneesJson;
    }
    return this.assigneeId !== this.initialAssigneeId
      || this.assignedStatus !== this.initialAssignedStatus
      || String(this.assignedDate) !== String(this.initialAssignedDate);
  }

  async refresh() {
    const child = this.operationChild;
    if (!child) return;

    const p = this.prefix;
    this.isNa = false;

    if (this.isMultiple) {
      // Lo stato NA in lettura è denormalizzato come `${p}Assigned === 'na'`
      // (con array assegnatari vuoto), non come `${p}Assignees === 'na'`.
      const flatAssigned = (child as any)[`${p}Assigned`];
      const assigneesRaw = (child as any)[`${p}Assignees`];
      if (flatAssigned === 'na' || assigneesRaw === 'na') {
        this.assigneesList = [];
        this.isNa = true;
      } else {
        const assignees = assigneesRaw || [];
        const parsed: AssignmentAssignee[] = typeof assignees === 'string' ? JSON.parse(assignees) : assignees;
        this.assigneesList = parsed.map((a: AssignmentAssignee) => ({
          assigneeId: a.assigneeId,
          status: a.status || 'todo',
          assignedDate: a.assignedDate,
        }));
      }
      this.initialAssigneesJson = JSON.stringify(this.assigneesList);
    } else {
      this.assigneeId = (child as any)[`${p}AssigneeId`] ?? null;
      this.assignedStatus = (child as any)[`${p}Assigned`] ?? 'todo';
      this.assignedDate = (child as any)[`${p}AssignedDate`] ?? null;

      this.initialAssigneeId = this.assigneeId;
      this.initialAssignedStatus = this.assignedStatus;
      this.initialAssignedDate = this.assignedDate;
    }

    this.initialIsNa = this.isNa;

    // Carica le note se configurate e l'utente ha il permesso di visualizzazione
    const cvName = this.notesCustomValueName;
    if (cvName && this.canShowNotes) {
      this.loadingNotes = true;
      try {
        const lead: Lead = await API.getLead(this.childId);
        this.notesValue = getLeadCustomValueText(lead, cvName) ?? '';
      } catch (e) {
        console.error('OperationAssignmentsDialog: refresh() error loading notes:', e); // eslint-disable-line no-console
        this.notesValue = '';
      } finally {
        this.loadingNotes = false;
      }
    } else {
      this.notesValue = '';
    }
    this.initialNotesValue = this.notesValue;
  }

  // --- Singolo assegnatario ---

  onStatusChange() {
    // La data non viene precompilata su 'done' (issue #1169): la inserisce l'utente.
    if (this.assignedStatus !== 'done') {
      this.assignedDate = null;
    }
    if (this.assignedStatus === 'na') {
      this.assigneeId = null;
      this.assignedDate = null;
    }
  }

  onAssigneeChange() {
    if (this.assigneeId === null || this.assigneeId === undefined) {
      this.assignedStatus = 'todo';
      this.assignedDate = null;
    }
  }

  onAssigneeClear() {
    this.assigneeId = null;
    this.assignedStatus = 'todo';
    this.assignedDate = null;
  }

  // --- Multi-assegnatario ---

  addAssignee() {
    this.assigneesList.push({ assigneeId: null, status: 'todo', assignedDate: null });
  }

  removeAssignee(index: number) {
    this.assigneesList.splice(index, 1);
  }

  // Lo switch "Non applicabile" pilota isNa (v-model). All'attivazione
  // svuota la lista assegnatari (NA è a livello di incarico).
  onNaToggle(val: boolean) {
    if (val) {
      this.assigneesList = [];
    }
  }

  // La data non viene precompilata su 'done' (issue #1169). Su 'todo' viene
  // azzerata per allinearsi al backend, che non persiste date su stato non assegnato.
  onMultiStatusChange(index: number) {
    const entry = this.assigneesList[index];
    if (entry.status !== 'done') {
      entry.assignedDate = null;
    }
  }

  // --- Date picker ---

  onClickDate() {
    this.datePickerTargetIndex = null;
    this.datePickerDate = this.assignedDate
      ? moment(this.assignedDate).toISOString(true).substring(0, 10)
      : null;
    this.datePickerDialog = true;
  }

  onClickMultiDate(index: number) {
    this.datePickerTargetIndex = index;
    const entry = this.assigneesList[index];
    this.datePickerDate = entry.assignedDate
      ? moment(entry.assignedDate).toISOString(true).substring(0, 10)
      : null;
    this.datePickerDialog = true;
  }

  onClearDate() {
    this.assignedDate = null;
  }

  onSelectDate(date: string | null) {
    this.datePickerDialog = false;
    const selectedDate = date ? moment(date, 'YYYY-MM-DD').toDate() : null;

    if (this.datePickerTargetIndex !== null) {
      const entry = this.assigneesList[this.datePickerTargetIndex];
      if (entry) entry.assignedDate = selectedDate;
    } else {
      this.assignedDate = selectedDate;
    }
  }

  // --- Save ---

  onSave() {
    if (!this.operationChild) return;

    const p = this.prefix;
    let body: Record<string, unknown>;
    const optimisticFields: Record<string, unknown> = {};

    if (this.isMultiple) {
      if (this.isNa) {
        body = { [`${p}Assignees`]: 'na' };
        // Optimistic: rispecchia la denormalizzazione backend per lo stato NA
        // (status flat 'na', array vuoto, campi flat azzerati) così la cella
        // mostra correttamente "Non applicabile".
        optimisticFields[`${p}Assignees`] = [];
        optimisticFields[`${p}Assigned`] = 'na';
        optimisticFields[`${p}AssigneeId`] = null;
        optimisticFields[`${p}AssigneeName`] = null;
        optimisticFields[`${p}AssignedDate`] = null;
      } else {
        body = {
          [`${p}Assignees`]: this.assigneesList.map((a) => ({
            assigneeId: a.assigneeId,
            status: a.status,
            assignedDate: a.assignedDate,
          })),
        };
        const withNames = this.assigneesList.map((a) => ({
          assigneeId: a.assigneeId,
          status: a.status,
          assignedDate: a.assignedDate,
          assigneeName: this.assigneeUsers.find((u) => u.id === a.assigneeId)?.name ?? null,
        }));
        // primary = primo 'done' oppure il primo (come extractAssignmentData backend),
        // per mantenere coerenti i campi flat usati dalla cella/dai filtri.
        const primary = withNames.find((a) => a.status === 'done') || withNames[0] || null;
        optimisticFields[`${p}Assignees`] = withNames;
        optimisticFields[`${p}Assigned`] = primary ? primary.status : null;
        optimisticFields[`${p}AssigneeId`] = primary ? primary.assigneeId : null;
        optimisticFields[`${p}AssigneeName`] = primary ? primary.assigneeName : null;
        optimisticFields[`${p}AssignedDate`] = primary ? primary.assignedDate : null;
      }
    } else {
      body = {
        [`${p}Assigned`]: this.assignedStatus,
        [`${p}Assignee`]: this.assigneeId ?? null,
        [`${p}AssignedDate`]: this.assignedDate ?? null,
      };
      optimisticFields[`${p}Assigned`] = this.assignedStatus;
      optimisticFields[`${p}AssigneeId`] = this.assigneeId ?? null;
      optimisticFields[`${p}AssigneeName`] = this.assigneeId
        ? (this.assigneeUsers.find((u) => u.id === this.assigneeId)?.name ?? null)
        : null;
      optimisticFields[`${p}AssignedDate`] = this.assignedDate ?? null;
    }

    // Modalità deferred: emette il body e lascia al genitore la gestione
    // (stage via updateTaskFields + flush al click di UpdateButton). Nessuna
    // API call qui, nessun optimistic commit sullo store operations.
    if (this.deferSave) {
      this.$emit('save', { body });
      this.dialog = false;
      return;
    }

    // Cattura i valori prima di chiudere il dialog (le prop possono cambiare dopo)
    const operationId = this.operation.id;

    // Optimistic update: aggiorna lo store e chiudi il dialog subito
    this.$store.commit('updateOperationChildFields', {
      operationId,
      childId: this.childId,
      fields: optimisticFields,
    });
    this.dialog = false;

    // Fire-and-forget: lancia le API call in background
    const promises: Promise<unknown>[] = [];

    if (this.operationChild.firstTaskId) {
      promises.push(API.updateLeadTask(this.childId, this.operationChild.firstTaskId, body));
    } else {
      promises.push(API.updateLeadAssignments(this.childId, body));
    }

    if (this.canEditNotes && this.notesCustomValueName && this.notesValue !== this.initialNotesValue) {
      promises.push(API.updateLeadAssignmentNotes(this.childId, {
        customValueName: this.notesCustomValueName,
        notes: this.notesValue,
      }));
    }

    Promise.all(promises)
      .then((results) => {
        if (results.some((r) => r instanceof Error)) {
          console.error('OperationAssignmentsDialog: background save error:', results); // eslint-disable-line no-console
          this.$store.dispatch('updateOperation', { id: operationId });
        }
      })
      .catch((e) => {
        console.error('OperationAssignmentsDialog: background save unexpected rejection:', e); // eslint-disable-line no-console
        this.$store.dispatch('updateOperation', { id: operationId });
      });
  }

  onClose() {
    this.dialog = false;
  }

  @Watch('value')
  onValueChange(val: boolean) {
    this.dialog = val;

    if (val) {
      this.refresh();
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }
}
